import { styled } from '@mui/material'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateOrderbookPrecisionAction } from 'store/orderbook/actions'
import { useOrderbookPrecision } from 'store/orderbook/hooks'
import { DropContentStyle } from 'styles'
import { useOnClickOutside } from 'usehooks-ts'
import { ReactComponent as ArrrowSvg } from './arrow.svg'
import { themeValue } from 'styles'
import { Fade } from '@mui/material'

const DecimalSelector = styled('div')<{ showPrecision: number }>`
  position: relative;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  position: relative;
  /* identical to box height, or 133% */

  letter-spacing: 0.025em;
  text-transform: uppercase;

  /* Controls/secondary-control-color/80 */

  /* color: ${(props) => props.theme.color.text40}; */
  cursor: pointer;

  .precision {
    color: ${(props) => props.theme.color.text90};
  }

  svg {
    color: ${({ theme }) => theme.color.text50};
    margin-left: 3px;
    transition: all 0.4s ease;
    transform: ${(props) =>
      props.showPrecision === 1 ? 'rotate(0deg)' : 'rotate(180deg)'};
  }

  &:hover {
    svg {
      color: ${({ theme }) => theme.color.primary30};
    }
  }

  &::after {
    content: '';
    width: 100%;
    height: 10px;
    position: absolute;
    left: 0px;
    bottom: -6px;
    background-color: transparent;
  }
`
const SelectorList = styled(DropContentStyle)`
  position: absolute;
  right: 0px;
  top: 100%;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  width: 71px;
  box-sizing: content-box;

  display: flex;
  flex-direction: column;

  z-index: 11;

  background-color: ${() =>
    themeValue('rgba(30, 35, 41, 1)', 'rgba(249, 249, 250, 1)')} !important;
  border-radius: 0px 0px 4px 4px;
  border: 1px solid;
  border-color: ${() =>
    themeValue('rgba(42, 49, 57, 1)', 'rgba(240, 242, 243, 1)')} !important;

  li {
    width: 100%;
    height: 26px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0 12px;
    padding-left: 25px;

    color: ${(props) => props.theme.color.text90};
    &:hover {
      color: ${({ theme }) => theme.palette.primary.main};
    }

    &.active {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`

// Generate precision data list.
// ['100', '10', '1', '0.1', '0.01', '0.001', ..., '0.0000000000000001']
const generatePrecisionList = (begin: number = 2, end: number = 16) => {
  const list = []
  for (let i = begin; i >= -end; i--) {
    if (i > 0) {
      list.push('1' + '0'.repeat(i))
    } else if (i === 0) {
      list.push('1')
    } else {
      list.push('0.' + '0'.repeat(-i - 1) + '1')
    }
  }
  return list
}
export const PRECISION = generatePrecisionList()

/* Preserve 4-digit precision option */
function getPrecisionOption(quoteScale: number) {
  const currentIndex = quoteScale + 2
  let start = currentIndex - 3 <= 0 ? 0 : currentIndex - 3
  let end =
    currentIndex + 1 <= PRECISION.length - 1
      ? currentIndex + 1
      : PRECISION.length - 1
  return PRECISION.slice(start, end)
}

export const OrderbookPrecision = memo<{ quoteScale: number }>(
  ({ quoteScale }) => {
    const [showPrecision, setShowPrecision] = useState<boolean>(false)
    const dispatch = useDispatch()
    const precision = useOrderbookPrecision()
    const selectorWrapRef = useRef<HTMLDivElement>(null)

    // const selectorPrecision = useCallback(() => {
    //   setShowPrecision(!showPrecision)
    // }, [setShowPrecision, showPrecision])

    const handleClickOutside = useCallback(() => {
      setShowPrecision(false)
    }, [])

    useOnClickOutside(selectorWrapRef, handleClickOutside)

    useEffect(() => {
      dispatch(
        updateOrderbookPrecisionAction(String(PRECISION[quoteScale + 2])),
      )
    }, [quoteScale])

    return (
      <DecimalSelector
        ref={selectorWrapRef}
        onMouseEnter={() => {
          setShowPrecision(true)
        }}
        onMouseLeave={() => {
          setShowPrecision(false)
        }}
        showPrecision={showPrecision ? 1 : 2}
      >
        <span className="precision">{precision}</span>
        <ArrrowSvg />
        {/* <Iconfont name="icon-ArrowDown" size={16} /> */}
        <Fade in={showPrecision}>
          <SelectorList style={{ display: showPrecision ? 'flex' : 'none' }}>
            {/* Preserve 4-digit precision option */}
            {[...getPrecisionOption(quoteScale)].reverse().map((item) => {
              return (
                <li
                  className={precision === item ? 'active' : ''}
                  onClick={() => {
                    dispatch(updateOrderbookPrecisionAction(item))
                  }}
                  key={item}
                >
                  {item}
                </li>
              )
            })}
          </SelectorList>
        </Fade>
      </DecimalSelector>
    )
  },
)
